import { IconUpload, IconFileText, IconChevronLeft, IconChevronRight, IconMinus, IconPlus, IconLoader2, IconAlertCircle, IconCheck, IconCloud, IconFileDescription } from "@tabler/icons-react";
import { useCallback, useState, useRef } from "react";
import { Viewer, Worker, SpecialZoomLevel, ScrollMode } from '@react-pdf-viewer/core';
import { pageNavigationPlugin, RenderCurrentPageLabelProps } from '@react-pdf-viewer/page-navigation';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';

export interface PDFAnalysis {
  summary: string;
  key_points: string[];
  topics: string[];
  learning_objectives?: string[];
}

export interface PDFOutlineItem {
  level: number;
  title: string;
  page_start?: number;
  page_end?: number;
}

export interface PDFContext {
  outline?: PDFOutlineItem[];
  focus_areas?: string[];
  glossary?: Array<{ term: string; definition: string }>;
  chapter_summaries?: Array<{ title: string; page_start?: number; summary: string }>;
}

export interface PDFBlockContent {
  // State
  status: 'empty' | 'uploading' | 'uploaded' | 'processing' | 'ready' | 'error';

  // File info (persisted)
  fileName?: string;
  fileSize?: number;
  pageCount?: number;

  // Upload progress
  uploadProgress?: number;      // 0-100
  uploadError?: string;

  // Supabase Storage (persisted)
  storagePath?: string;
  storageUrl?: string;

  // In-memory only (not persisted)
  blobUrl?: string;

  // Processing state
  processing?: {
    state: 'uploading' | 'processing' | 'ready' | 'error';
    started_at?: string;
    completed_at?: string;
    ocr_used?: boolean;
  };

  // AI Analysis (from Gemini)
  analyzing?: boolean;
  analysis?: PDFAnalysis;

  // Rich context for AI consumption
  pdf_context?: PDFContext;

  // Extracted text storage pointers
  text_assets?: {
    extracted_text_path?: string;
    extracted_text_sha256?: string;
    total_pages?: number;
    total_chars?: number;
  };

  // Error state
  error?: string;
}


interface PDFBlockProps {
  // The URL to display the PDF from (blob URL for local, storage URL for cloud)
  pdfUrl?: string;
  fileName?: string;
  // Content with analysis
  content?: PDFBlockContent;
  // Upload state
  isUploading?: boolean;
  uploadProgress?: number;
  uploadError?: string;
  isCloudSynced?: boolean;
  // Callbacks
  onFileSelect?: (file: File, blobUrl: string) => void;
  onOpenInTab?: () => void;
}

export const PDFBlock = ({
  pdfUrl: externalPdfUrl,
  fileName: externalFileName,
  content,
  isUploading,
  uploadError,
  isCloudSynced,
  onFileSelect,
}: PDFBlockProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Analysis state derived from content
  const isAnalyzing = content?.analyzing === true;
  const hasAnalysis = content?.analysis != null;
  const analysisError = content?.error && !hasAnalysis;

  // LOCAL blob URL state - managed entirely within this component
  // This survives parent re-renders and React Query refetches
  const [localBlobUrl, setLocalBlobUrl] = useState<string | null>(null);
  const [localFileName, setLocalFileName] = useState<string | null>(null);

  // Use local blob URL if available (for immediate viewing), otherwise use external URL (from storage)
  const displayUrl = localBlobUrl || externalPdfUrl;
  const displayFileName = localFileName || externalFileName;

  // Create plugin instances at top level (they use hooks internally)
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const zoomPluginInstance = zoomPlugin();

  const { CurrentPageLabel, GoToNextPage, GoToPreviousPage, NumberOfPages } = pageNavigationPluginInstance;
  const { ZoomIn, ZoomOut, CurrentScale } = zoomPluginInstance;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      const blobUrl = URL.createObjectURL(file);
      console.log('PDF file selected, created blob URL:', blobUrl);

      // Set local state IMMEDIATELY for instant display
      setLocalBlobUrl(blobUrl);
      setLocalFileName(file.name);

      // Notify parent for upload handling
      onFileSelect?.(file, blobUrl);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type === 'application/pdf') {
      const blobUrl = URL.createObjectURL(file);
      console.log('PDF file dropped, created blob URL:', blobUrl);

      // Set local state IMMEDIATELY for instant display
      setLocalBlobUrl(blobUrl);
      setLocalFileName(file.name);

      // Notify parent for upload handling
      onFileSelect?.(file, blobUrl);
    }
  }, [onFileSelect]);

  // If we have a PDF URL (local blob or storage), show the viewer
  if (displayUrl) {
    return (
      <div ref={containerRef} className="pt-4 space-y-3" tabIndex={0}>
        {/* Mini toolbar */}
        <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
          {/* Page Navigation - Left */}
          <div className="flex items-center gap-1">
            <GoToPreviousPage>
              {(props) => (
                <button
                  onClick={props.onClick}
                  disabled={props.isDisabled}
                  className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Previous page (←)"
                >
                  <IconChevronLeft className="w-4 h-4 text-slate-600" />
                </button>
              )}
            </GoToPreviousPage>

            <div className="text-sm text-slate-600 font-medium min-w-[70px] text-center flex items-center justify-center gap-1">
              <CurrentPageLabel>
                {(props: RenderCurrentPageLabelProps) => (
                  <span>{props.currentPage + 1}</span>
                )}
              </CurrentPageLabel>
              <span>of</span>
              <NumberOfPages>
                {(props) => (
                  <span>{props.numberOfPages}</span>
                )}
              </NumberOfPages>
            </div>

            <GoToNextPage>
              {(props) => (
                <button
                  onClick={props.onClick}
                  disabled={props.isDisabled}
                  className="p-1.5 rounded-md hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Next page (→)"
                >
                  <IconChevronRight className="w-4 h-4 text-slate-600" />
                </button>
              )}
            </GoToNextPage>
          </div>

          {/* File name & Upload Status - Center */}
          <div className="flex items-center gap-2 flex-1 mx-4 min-w-0">
            <IconFileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="text-xs text-slate-500 truncate">{displayFileName}</span>

            {/* Upload status indicator */}
            {isUploading && (
              <div className="flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 rounded-full">
                <IconLoader2 className="w-3 h-3 text-blue-500 animate-spin" />
                <span className="text-xs text-blue-600">Uploading...</span>
              </div>
            )}
            {!isUploading && isCloudSynced && (
              <div className="flex items-center gap-1 px-2 py-0.5 bg-green-50 rounded-full" title="Saved to cloud">
                <IconCloud className="w-3 h-3 text-green-500" />
                <IconCheck className="w-3 h-3 text-green-500" />
              </div>
            )}
            {uploadError && (
              <div className="flex items-center gap-1 px-2 py-0.5 bg-red-50 rounded-full" title={uploadError}>
                <IconAlertCircle className="w-3 h-3 text-red-500" />
                <span className="text-xs text-red-600">Upload failed</span>
              </div>
            )}
          </div>

          {/* Zoom Controls - Right */}
          <div className="flex items-center gap-1">
            <ZoomOut>
              {(props) => (
                <button
                  onClick={props.onClick}
                  className="p-1.5 rounded-md hover:bg-slate-200 transition-colors"
                  title="Zoom out"
                >
                  <IconMinus className="w-4 h-4 text-slate-600" />
                </button>
              )}
            </ZoomOut>

            <CurrentScale>
              {(props) => (
                <span className="text-xs text-slate-500 min-w-[40px] text-center">
                  {Math.round(props.scale * 100)}%
                </span>
              )}
            </CurrentScale>

            <ZoomIn>
              {(props) => (
                <button
                  onClick={props.onClick}
                  className="p-1.5 rounded-md hover:bg-slate-200 transition-colors"
                  title="Zoom in"
                >
                  <IconPlus className="w-4 h-4 text-slate-600" />
                </button>
              )}
            </ZoomIn>
          </div>
        </div>

        {/* PDF Viewer - Single page view */}
        <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-[#525659]">
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <div className="h-[650px] [&_.rpv-core__viewer]:h-full [&_.rpv-core__inner-pages]:!overflow-hidden">
              <Viewer
                fileUrl={displayUrl}
                defaultScale={SpecialZoomLevel.PageFit}
                scrollMode={ScrollMode.Page}
                plugins={[pageNavigationPluginInstance, zoomPluginInstance]}
                renderError={(error) => (
                  <div className="flex items-center justify-center h-full bg-slate-800/90">
                    <div className="text-center space-y-3">
                      <IconAlertCircle className="w-8 h-8 text-red-400 mx-auto" />
                      <p className="text-sm text-white">Failed to load PDF</p>
                      <p className="text-xs text-slate-400">{error.message}</p>
                    </div>
                  </div>
                )}
              />
            </div>
          </Worker>
        </div>

        {/* Analysis Status Bar */}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 text-slate-400">
            <IconFileText className="w-3.5 h-3.5" />
            <span className="truncate max-w-[200px]">{displayFileName}</span>
            {content?.text_assets?.total_pages && (
              <span className="text-slate-300">• {content.text_assets.total_pages} pages</span>
            )}
          </div>

          {/* Analysis status */}
          <div className="flex items-center gap-2">
            {isAnalyzing && (
              <span className="flex items-center gap-1.5 text-blue-500">
                <IconLoader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Analyzing...</span>
              </span>
            )}
            {!isAnalyzing && hasAnalysis && (
              <button
                onClick={() => setShowAnalysis(!showAnalysis)}
                className="flex items-center gap-1.5 text-emerald-600 hover:text-emerald-700 transition-colors"
              >
                <IconFileDescription className="w-3.5 h-3.5" />
                <span>{showAnalysis ? 'Hide' : 'Show'} summary</span>
              </button>
            )}
            {!isAnalyzing && analysisError && (
              <span className="flex items-center gap-1.5 text-amber-500">
                <IconAlertCircle className="w-3.5 h-3.5" />
                <span>Analysis failed</span>
              </span>
            )}
          </div>
        </div>

        {/* Analysis Panel */}
        {showAnalysis && content?.analysis && (
          <div className="space-y-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
            {/* Summary */}
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Summary</h4>
              <p className="text-sm text-slate-600 leading-relaxed">{content.analysis.summary}</p>
            </div>

            {/* Key Points */}
            {content.analysis.key_points && content.analysis.key_points.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Key Points</h4>
                <ul className="space-y-1">
                  {content.analysis.key_points.map((point, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-emerald-500 mt-0.5">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Topics */}
            {content.analysis.topics && content.analysis.topics.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Topics</h4>
                <div className="flex flex-wrap gap-1.5">
                  {content.analysis.topics.map((topic, i) => (
                    <span key={i} className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full text-xs">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Learning Objectives */}
            {content.analysis.learning_objectives && content.analysis.learning_objectives.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">What you'll learn</h4>
                <ul className="space-y-1">
                  {content.analysis.learning_objectives.map((obj, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-blue-500 mt-0.5">✓</span>
                      <span>{obj}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Text extraction info */}
            {content.text_assets?.total_chars && (
              <div className="pt-2 border-t border-slate-200 text-xs text-slate-400">
                Extracted: {content.text_assets.total_chars.toLocaleString()} characters from {content.text_assets.total_pages} pages
                {content.processing?.ocr_used && ' (OCR used)'}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Upload UI when no PDF is loaded
  return (
    <div className="pt-4">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${isDragging
          ? 'border-slate-400 bg-slate-50'
          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/50'
          }`}
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
            <IconFileText className="w-6 h-6 text-slate-400" />
          </div>

          <div className="space-y-1">
            <p className="text-sm font-medium text-slate-600">
              Drop a PDF here or
            </p>
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg cursor-pointer hover:bg-slate-800 transition-colors">
              <IconUpload className="w-4 h-4" />
              <span>Choose File</span>
              <input
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          </div>

          <p className="text-xs text-slate-400">
            PDF files up to 50MB
          </p>
        </div>
      </div>
    </div>
  );
};
